import { baseApi } from './baseApi';
import type { ConversationWithDetails, MessageWithSender } from '@nimbazaar/types';
import { API_ENDPOINTS } from '@nimbazaar/constants';

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

interface PaginatedApiResponse<T> extends ApiResponse<T> {
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

interface StartConversationRequest {
  adId: string;
  sellerId: string;
  message?: string;
}

interface SendMessageRequest {
  conversationId: string;
  content: string;
  type?: 'TEXT' | 'IMAGE';
  imageUrl?: string;
}

interface EditMessageRequest {
  conversationId: string;
  messageId: string;
  content: string;
  type?: 'TEXT' | 'IMAGE';
  imageUrl?: string;
}

interface DeleteMessageRequest {
  conversationId: string;
  messageId: string;
}

export const chatApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConversations: builder.query<
      PaginatedApiResponse<ConversationWithDetails[]>,
      { page?: number; limit?: number } | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.set('page', String(params.page));
        if (params?.limit) searchParams.set('limit', String(params.limit));
        const qs = searchParams.toString();
        return `${API_ENDPOINTS.CHAT.CONVERSATIONS}${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['Conversations'],
    }),

    getConversation: builder.query<ApiResponse<ConversationWithDetails>, string>({
      query: (id) => API_ENDPOINTS.CHAT.CONVERSATION_DETAIL(id),
      providesTags: (result, error, id) => [{ type: 'Conversations', id }],
    }),

    getMessages: builder.query<
      PaginatedApiResponse<MessageWithSender[]>,
      { conversationId: string; page?: number; limit?: number }
    >({
      query: ({ conversationId, page = 1, limit = 50 }) =>
        `${API_ENDPOINTS.CHAT.MESSAGES(conversationId)}?page=${page}&limit=${limit}`,
      providesTags: (result, error, { conversationId }) => [
        { type: 'Messages', id: conversationId },
      ],
    }),

    sendMessage: builder.mutation<ApiResponse<MessageWithSender>, SendMessageRequest>({
      query: ({ conversationId, ...body }) => ({
        url: API_ENDPOINTS.CHAT.MESSAGES(conversationId),
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { conversationId }) => [
        { type: 'Messages', id: conversationId },
        'Conversations',
      ],
    }),

    startConversation: builder.mutation<
      ApiResponse<ConversationWithDetails>,
      StartConversationRequest
    >({
      query: (body) => ({
        url: API_ENDPOINTS.CHAT.START,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Conversations'],
    }),

    markAsRead: builder.mutation<ApiResponse<{ count: number }>, string>({
      query: (conversationId) => ({
        url: API_ENDPOINTS.CHAT.MARK_READ(conversationId),
        method: 'POST',
      }),
      invalidatesTags: (result, error, conversationId) => [
        { type: 'Messages', id: conversationId },
        'UnreadCount',
        'Conversations',
      ],
    }),

    getUnreadCount: builder.query<ApiResponse<{ count: number }>, void>({
      query: () => API_ENDPOINTS.CHAT.UNREAD_COUNT,
      providesTags: ['UnreadCount'],
    }),

    editMessage: builder.mutation<ApiResponse<MessageWithSender>, EditMessageRequest>({
      query: ({ conversationId, messageId, ...body }) => ({
        url: API_ENDPOINTS.CHAT.MESSAGE_DETAIL(conversationId, messageId),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { conversationId }) => [
        { type: 'Messages', id: conversationId },
        'Conversations',
      ],
    }),

    deleteMessage: builder.mutation<ApiResponse<MessageWithSender>, DeleteMessageRequest>({
      query: ({ conversationId, messageId }) => ({
        url: API_ENDPOINTS.CHAT.MESSAGE_DETAIL(conversationId, messageId),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { conversationId }) => [
        { type: 'Messages', id: conversationId },
        'Conversations',
      ],
    }),
  }),
});

export const {
  useGetConversationsQuery,
  useGetConversationQuery,
  useGetMessagesQuery,
  useSendMessageMutation,
  useStartConversationMutation,
  useMarkAsReadMutation,
  useGetUnreadCountQuery,
  useEditMessageMutation,
  useDeleteMessageMutation,
} = chatApi;
